import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { listAppointments } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import { date } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Filter } from '../lib/list'
import AppointmentForm from '../components/AppointmentForm'
import type { Appointment } from '../types'

const PAGE_SIZE = 25

type TimeChip = 'this_week' | 'this_month' | 'future' | 'all'

function getChipFilters(chip: TimeChip): Filter[] {
  const today = new Date().toISOString().slice(0, 10)
  if (chip === 'this_week') {
    const d = new Date()
    const day = d.getDay()
    const mon = new Date(d); mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1))
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
    return [
      { column: 'appointment_date', op: 'gte', value: mon.toISOString().slice(0, 10) },
      { column: 'appointment_date', op: 'lte', value: sun.toISOString().slice(0, 10) },
    ]
  }
  if (chip === 'this_month') {
    const d = new Date()
    const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10)
    return [
      { column: 'appointment_date', op: 'gte', value: first },
      { column: 'appointment_date', op: 'lte', value: last },
    ]
  }
  if (chip === 'future') {
    return [{ column: 'appointment_date', op: 'gte', value: today }]
  }
  return []
}

const CHIPS: { key: TimeChip; label: string }[] = [
  { key: 'future', label: 'Future' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'all', label: 'All' },
]

export default function Appointments() {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [chip, setChip] = useState<TimeChip>('future')

  const defaultSort = chip === 'future' || chip === 'this_week' || chip === 'this_month'
    ? { column: 'appointment_date', dir: 'asc' as const }
    : { column: 'appointment_date', dir: 'desc' as const }

  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort } =
    useServerList<Appointment>(listAppointments, { pageSize: PAGE_SIZE, defaultSort })

  useEffect(() => {
    setFilters(getChipFilters(chip))
  }, [chip]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader title="Appointments" subtitle={`${count.toLocaleString()} total`}
        actions={<Button onClick={() => setCreating(true)}><Plus size={16} /> New Appointment</Button>} />

      {creating && (
        <AppointmentForm
          onClose={() => setCreating(false)}
          onSaved={a => { setCreating(false); navigate(`/appointments/${a.id}`) }}
        />
      )}
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {CHIPS.map(c => (
              <button
                key={c.key}
                onClick={() => setChip(c.key)}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  chip === c.key
                    ? 'bg-green-700 text-white border-green-700'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-green-600 hover:text-green-700'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <SearchInput value={search} onChange={setSearch} placeholder="Search customer, designer, or comments…" />
          <FilterBar
            onChange={f => setFilters([...getChipFilters(chip), ...f])}
            fields={[
              { key: 'goahead', label: 'Go Ahead', column: 'go_ahead', type: 'boolean' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading appointments…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={a => navigate(`/appointments/${a.id}`)}
              columns={[
                { key: 'legacy_id', header: 'Appt ID', render: a => a.legacy_id ?? '—', sortKey: 'legacy_id' },
                { key: 'appointment_date', header: 'Date', render: a => a.appointment_date ? date(a.appointment_date) : '—', sortKey: 'appointment_date' },
                { key: 'customer_name', header: 'Customer', render: a => a.customer_name || <span className="text-gray-400">—</span>, sortKey: 'customer_name' },
                { key: 'designer_name', header: 'Designer', render: a => a.designer_name || <span className="text-gray-400">—</span>, sortKey: 'designer_name' },
                { key: 'comments', header: 'Comments', render: a => <span className="line-clamp-1">{a.comments || '—'}</span> },
                { key: 'go_ahead', header: 'Go Ahead', render: a => a.go_ahead
                  ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Yes</span>
                  : <span className="text-gray-400 text-xs">No</span> },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}
